import { useNavigate, useSearch } from '@tanstack/react-router';
import { Loader2 } from 'lucide-react';
import { useEffect, useRef } from 'react';

import { useAuthStore } from '~/store/auth.store';

import { performLogout } from '../utils/perform-logout';

/**
 * Entry point for the tokenized verification link. Logs out any currently
 * signed-in user (the link is addressed to the invited user), then forwards
 * to the set-password page carrying the link identifiers. The email is NOT
 * verified here — the set-password call verifies and sets the password in one
 * step so the one-time token is consumed exactly once.
 */
export function VerifyEmailPage() {
  const navigate = useNavigate();
  const { user_id: userId, token } = useSearch({ from: '/verify-email' });
  const calledRef = useRef(false);

  useEffect(() => {
    // Prevent double-invocation from React StrictMode
    if (calledRef.current) return;
    calledRef.current = true;

    if (!userId || !token) {
      void navigate({
        to: '/login',
        search: { error: 'Invalid verification link.' },
        replace: true,
      });
      return;
    }

    // Snapshot auth state at the moment the effect runs (not reactive) so a
    // subsequent logout state-change cannot re-trigger this one-shot effect.
    const isAuthenticated = useAuthStore.getState().isAuthenticated;

    // Log out any currently-authenticated user before forwarding — the link
    // is addressed to the invited user, not whoever happens to be signed in.
    const logoutIfNeeded = isAuthenticated ? performLogout() : Promise.resolve();

    void logoutIfNeeded.then(() => {
      // Forward the link identifiers so the set-password page can verify the
      // email and set the password in a single token-consuming request.
      void navigate({
        to: '/reset-password',
        search: { user_id: userId, token },
        replace: true,
      });
    });
  }, [userId, token, navigate]);

  return (
    <div className="flex h-dvh items-center justify-center">
      <Loader2 className="text-primary h-8 w-8 animate-spin" />
    </div>
  );
}
